/**
 * SEO component for setting page metadata including
 * Open Graph, Twitter Cards, favicons, and structured data
 */

import React from 'react'
import Helmet from 'react-helmet'
import { getTextContent } from 'src/modules/utils/text'

const FAVICON_BASE_URL = 'https://assets-imgx.fubo.tv/favicons'

function SEO({ description, title, image, lang = 'en', canonical }) {
  const metaDescription =
    description ||
    `Fubo is the world’s only sports-focused live TV streaming service with top leagues and teams, plus popular shows, movies and news for the entire household.`

  const metadataImg =
    image ||
    'https://assets-imgx.fubo.tv/marketing/lp/images/meta/metadata_Fubo.jpg?auto=format,compress'

  const titleStr = getTextContent(title)
  return (
    <Helmet
      htmlAttributes={{
        lang: lang === 'en' ? 'en-US' : 'es-US',
      }}
      script={[
        {
          type: 'text/javascript',
          innerHTML: 'window.timeStampOnPageLoad = new Date().getTime();',
        },
        {
          type: 'application/ld+json',
          dangerouslySetInnerHTML: {
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Organization',
              name: 'Fubo',
              url: 'https://www.fubo.tv',
              logo: 'https://assets-imgx.fubo.tv/marketing/lp/images/logos/Fubo_Primary_Logo_FC_RGB.png',
              sameAs: [
                'https://www.facebook.com/fuboTV',
                'https://x.com/fuboTV',
                'https://www.instagram.com/fuboTV',
                'https://www.linkedin.com/company/fuboTV',
              ],
              foundingDate: '2015',
              founder: [
                {
                  '@type': 'Person',
                  name: 'David Gandler',
                },
                {
                  '@type': 'Person',
                  name: 'Alberto Horihuela',
                },
                {
                  '@type': 'Person',
                  name: 'Sung Ho Choi',
                },
              ],
              address: {
                '@type': 'PostalAddress',
                streetAddress: '1290 6th Ave',
                addressLocality: 'New York',
                addressRegion: 'NY',
                postalCode: '10104',
                addressCountry: 'US',
              },
              contactPoint: {
                '@type': 'ContactPoint',
                contactType: 'Customer Support',
                url: 'https://support.fubo.tv',
                availableLanguage: ['English', 'Spanish'],
              },
            }),
          },
        },
      ]}
      title={titleStr}
      titleTemplate={`%s | Fubo`}
      link={[
        {
          rel: 'icon',
          type: 'image/x-icon',
          href: `${FAVICON_BASE_URL}/favicon.ico`,
        },
        {
          rel: 'icon',
          type: 'image/png',
          sizes: '32x32',
          href: `${FAVICON_BASE_URL}/favicon-32x32.png`,
        },
        {
          rel: 'icon',
          type: 'image/png',
          sizes: '16x16',
          href: `${FAVICON_BASE_URL}/favicon-16x16.png`,
        },
        {
          rel: 'apple-touch-icon',
          sizes: '180x180',
          href: `${FAVICON_BASE_URL}/apple-touch-icon-180x180.png`,
        },
        {
          rel: 'manifest',
          href: `${FAVICON_BASE_URL}/manifest.webmanifest`,
        },
        ...(canonical
          ? [
              {
                rel: 'canonical',
                href: canonical,
              },
            ]
          : []),
      ]}
      meta={[
        {
          name: 'viewport',
          content: 'width=device-width, initial-scale=1, shrink-to-fit=no',
        },
        {
          name: `description`,
          content: metaDescription,
        },
        {
          property: `fb:app_id`,
          content: '162788457265492',
        },
        {
          property: `og:title`,
          content: titleStr,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          property: `og:site_name`,
          content: 'Fubo',
        },
        {
          property: `og:url`,
          content: typeof window !== 'undefined' ? window.location.href : 'https://www.fubo.tv',
        },
        {
          property: `og:image`,
          content: metadataImg,
        },
        {
          property: `og:image:width`,
          content: '1200',
        },
        {
          property: `og:image:height`,
          content: '628',
        },
        {
          name: `twitter:card`,
          content: `summary_large_image`,
        },
        {
          name: `twitter:site`,
          content: '@fuboTV',
        },
        {
          name: `twitter:creator`,
          content: '@fuboTV',
        },
        {
          name: `twitter:title`,
          content: titleStr,
        },
        {
          name: `twitter:image`,
          content: metadataImg,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
        {
          name: 'theme-color',
          content: '#ffffff',
        },
        {
          name: 'application-name',
          content: 'stream',
        },
        {
          name: 'apple-mobile-web-app-capable',
          content: 'yes',
        },
        {
          name: 'apple-mobile-web-app-status-bar-style',
          content: 'black-translucent',
        },
        {
          name: 'apple-mobile-web-app-title',
          content: 'stream',
        },
        {
          name: 'msapplication-TileColor',
          content: '#ffffff',
        },
        {
          name: 'msapplication-TileImage',
          content: `${FAVICON_BASE_URL}/mstile-144x144.png`,
        },
        {
          name: 'msapplication-config',
          content: `${FAVICON_BASE_URL}/browserconfig.xml`,
        },
      ]}
    />
  )
}

export default SEO
